/**
 * Field Renderers
 *
 * Thin wrappers that render Oasis form components from FieldSchema definitions.
 * Each renderer receives the react-hook-form control and the field schema.
 */
import {
  CalendarPicker,
  FormCheckbox,
  FormDatePicker,
  FormIncrementInput,
  FormInput,
  FormNewSelect,
  FormTextArea,
  Paper,
  apiDateFormat,
} from "@chhjit/react-components";
import { Divider, Grid, Typography } from "@material-ui/core";
import { useStore } from "effector-react";
import moment, { type Moment } from "moment";
import { memo, useEffect } from "react";
import { useController } from "react-hook-form";

import { useFeatureFlagsWithAttributes } from "../../../../entities/locations";
import { $states, getStatesFx } from "../../../../entities/states";
import {
  Bedroom1Icon,
  Bedroom2Icon,
  Bedroom3Icon,
  Bedroom4Icon,
  BedroomStudioIcon,
  BinderPresentationIcon,
  FewItemsIcon,
  HouseCleanupIcon,
  RoomFullOfItemsIcon,
  SingleItemIcon,
  SupportOperatorIcon,
} from "../../../assets";
import { BlockTextProvider } from "../../../context/step-config-context";
import type { ContentBlock, FieldSchema } from "../../../types/workflow-schema";
import { AddressFields } from "../../../ui/form-fields/address-fields";
import { TileSelect } from "../../../ui/form-fields/tile-select";
import { AddressDisplayBlock } from "./address-display";
import { DwellingInfoBlock } from "./dwelling-info";
import { PostalInputBlock } from "./postal-input";
import { PromoCodeFieldRenderer } from "./promo-code";
import { SmsConsentBlock } from "./sms-consent";

// Maps field name → option value → icon Element
const TILE_ICON_MAP: Record<string, Record<string, JSX.Element>> = {
  typeOfJunk: {
    "Single Item": <SingleItemIcon />,
    "A Few Items": <FewItemsIcon />,
    "Room Full of Items": <RoomFullOfItemsIcon />,
    "Full House/Office Clean Up": <HouseCleanupIcon />,
  },
  bookingType: {
    call: <SupportOperatorIcon />,
    online: <BinderPresentationIcon />,
  },
  numberBedroom: {
    studio: <BedroomStudioIcon />,
    "1": <Bedroom1Icon />,
    "2": <Bedroom2Icon />,
    "3": <Bedroom3Icon />,
    "4": <Bedroom4Icon />,
  },
};

// ──────────────────────────────────────────────
// Common props passed to every field renderer
// ──────────────────────────────────────────────

export type FieldRendererProps = {
  field: FieldSchema;
  control: any; // react-hook-form Control
};

// ──────────────────────────────────────────────
// Field type → renderer component mapping
// ──────────────────────────────────────────────

const TextFieldRenderer = memo(({ field, control }: FieldRendererProps) => (
  <FormInput
    id={`ob-form-${field.name}`}
    control={control}
    name={field.name}
    label={field.label}
    placeholder={field.placeholder}
    required={field.required}
  />
));

const preventSpaces = (e: React.KeyboardEvent) => {
  if (e.key === " ") e.preventDefault();
};

const EmailFieldRenderer = memo(({ field, control }: FieldRendererProps) => (
  <FormInput
    id={`ob-form-${field.name}`}
    control={control}
    name={field.name}
    label={field.label}
    placeholder={field.placeholder}
    required={field.required}
    type="email"
    inputProps={{ onKeyDown: preventSpaces }}
  />
));

const PhoneFieldRenderer = memo(({ field, control }: FieldRendererProps) => (
  <FormInput
    id={`ob-form-${field.name}`}
    control={control}
    name={field.name}
    label={field.label}
    placeholder={field.placeholder}
    required={field.required}
    mask="phone"
  />
));

const SelectFieldRenderer = memo(({ field, control }: FieldRendererProps) => (
  <FormNewSelect
    control={control}
    name={field.name}
    label={field.label}
    options={
      field.options?.map((o) => ({ value: o.value, label: o.label })) ?? []
    }
    required={field.required}
    menuPortalTarget={document.body}
  />
));

const CheckboxFieldRenderer = memo(({ field, control }: FieldRendererProps) => (
  <FormCheckbox
    id={`ob-form-${field.name}`}
    control={control}
    name={field.name}
    label={field.label}
    variant="outline"
  />
));

const DateFieldRenderer = memo(({ field, control }: FieldRendererProps) => (
  <FormDatePicker
    id={`ob-form-${field.name}`}
    control={control}
    name={field.name}
    label={field.label}
    required={field.required}
  />
));

const NumberFieldRenderer = memo(({ field, control }: FieldRendererProps) => (
  <FormInput
    id={`ob-form-${field.name}`}
    control={control}
    name={field.name}
    label={field.label}
    placeholder={field.placeholder}
    required={field.required}
    type="number"
  />
));

const TextareaFieldRenderer = memo(({ field, control }: FieldRendererProps) => (
  <FormTextArea
    id={`ob-form-${field.name}`}
    control={control}
    name={field.name}
    label={field.label}
    placeholder={field.placeholder}
    required={field.required}
  />
));

const TileSelectFieldRenderer = memo(
  ({ field, control }: FieldRendererProps) => {
    const iconMap = TILE_ICON_MAP[field.name];

    return (
      <TileSelect
        control={control}
        name={field.name}
        options={(field.options ?? []).map((o) => ({
          value: o.value,
          label: o.label,
          icon: iconMap?.[o.value] ?? <></>,
        }))}
        required={field.required}
      />
    );
  },
);

const IncrementFieldRenderer = memo(
  ({ field, control }: FieldRendererProps) => (
    <div>
      <Typography
        variant="subtitle1"
        component="label"
        htmlFor={`ob-form-${field.name}`}
        style={{ display: "block", marginBottom: 8 }}
      >
        {field.label}
      </Typography>
      <FormIncrementInput
        id={`ob-form-${field.name}`}
        control={control}
        name={field.name}
        min={0}
        aria-label={field.label}
      />
    </div>
  ),
);

/**
 * Address field renderer — self-contained composite field.
 *
 * Renders Google Places autocomplete with "I can't find my address" link
 * and manual address entry fallback. Loads states internally.
 *
 * The parent form MUST use FormProvider so AddressFields can access
 * useFormContext(). Sub-field names are derived from field.name:
 *   "origin" → origin, origin2, originCity, originState, originPostal, originCountry
 *   "destination" → destination, destination2, destinationCity, ...
 *   "address" (legacy) → address, address2, city, state, postal, country
 */
const AddressFieldRenderer = memo(
  ({ field, control: _control }: FieldRendererProps) => {
    const { states, loading: statesLoading } = useStore($states);

    const { toggles } = useFeatureFlagsWithAttributes();
    /** php-api -> hunkware-api cutover for this caller. Flag off === the legacy path. */
    const useNode = Boolean(
      toggles?.HunkWare_Geolocation_Address_Services_Node_Migration,
    );

    useEffect(() => {
      if (!states.length) {
        getStatesFx({ useNode });
      }
    }, [states.length, useNode]);

    return (
      <AddressFields
        states={states}
        statesLoading={statesLoading}
        baseName={field.name}
      />
    );
  },
);

/**
 * Calendar field renderer — full inline month calendar.
 *
 * Uses CalendarPicker (full month view) instead of FormDatePicker (text input).
 * Defaults to tomorrow on first render when no stored value exists.
 */
const CalendarFieldRenderer = memo(({ field, control }: FieldRendererProps) => {
  const {
    field: { value, onChange },
  } = useController({ control, name: field.name });

  // Default to tomorrow if no stored value
  useEffect(() => {
    if (!value) {
      onChange(moment().add(1, "day").format(apiDateFormat));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (date: Moment) => {
    onChange(moment(date).format(apiDateFormat));
  };

  return (
    <Paper elevation={16}>
      <div style={{ paddingTop: 26 }}>
        <CalendarPicker
          onChange={handleChange}
          date={value ? moment(value as string) : moment().add(1, "day")}
          minDate={moment()}
        />
      </div>
    </Paper>
  );
});

/**
 * Hidden field renderer — renders nothing.
 *
 * The field value (always the schema's defaultValue) is registered in the
 * form and synced to the store on explicit submit only (excluded from
 * the debounced sync in SchemaFormRenderer). Used as a declarative signal
 * to trigger API contracts without tying the action to a specific step or
 * component (e.g. bookingConfirmed on the truck-loads step).
 */
const HiddenFieldRenderer = memo(({ field, control }: FieldRendererProps) => {
  useController({ control, name: field.name });
  return null;
});

/**
 * Postal input field renderer — zip/postal code with API confirmation gate.
 *
 * Delegates to PostalInputBlock, which registers two internal form fields:
 *   _postalDisplay — visible typed input
 *   servicePostal  — truthy once the postal API confirms; gated by Yup
 *
 * The Grid item wrapper is provided by FieldBlockRenderer (not this renderer).
 */
const PostalInputFieldRenderer = memo(
  ({ field, control }: FieldRendererProps) => (
    <PostalInputBlock
      control={control}
      label={field.label}
      required={field.required}
    />
  ),
);

// ──────────────────────────────────────────────
// Registry
// ──────────────────────────────────────────────

export const fieldRendererRegistry: Record<
  string,
  React.ComponentType<FieldRendererProps>
> = {
  text: TextFieldRenderer,
  email: EmailFieldRenderer,
  phone: PhoneFieldRenderer,
  select: SelectFieldRenderer,
  checkbox: CheckboxFieldRenderer,
  date: DateFieldRenderer,
  number: NumberFieldRenderer,
  textarea: TextareaFieldRenderer,
  address: AddressFieldRenderer,
  "tile-select": TileSelectFieldRenderer,
  increment: IncrementFieldRenderer,
  calendar: CalendarFieldRenderer,
  "postal-input": PostalInputFieldRenderer,
  "promo-code": PromoCodeFieldRenderer,
  hidden: HiddenFieldRenderer,
};

// ──────────────────────────────────────────────
// Content block renderers (non-field blocks)
// ──────────────────────────────────────────────

type ContentBlockRendererProps = {
  block: ContentBlock;
  control?: any;
};

export const ContentBlockRenderer = memo(
  ({ block, control }: ContentBlockRendererProps) => {
    switch (block.blockType) {
      case "field": {
        const Renderer = fieldRendererRegistry[block.type];
        if (!Renderer) return null;
        const { blockType, ...fieldSchema } = block;
        return (
          <Grid
            item
            xs={(block.gridSize?.xs ?? 12) as any}
            sm={block.gridSize?.sm as any}
            md={block.gridSize?.md as any}
          >
            <BlockTextProvider textBlocks={block.textBlocks}>
              <Renderer field={fieldSchema} control={control} />
            </BlockTextProvider>
          </Grid>
        );
      }
      case "text":
        return (
          <Grid item xs={12}>
            {block.html ? (
              <Typography
                variant={block.variant || "body1"}
                align={block.align || "left"}
                dangerouslySetInnerHTML={{ __html: block.content }}
              />
            ) : (
              <Typography
                variant={block.variant || "body1"}
                align={block.align || "left"}
              >
                {block.content}
              </Typography>
            )}
          </Grid>
        );
      case "divider":
        return (
          <Grid item xs={12}>
            <Divider />
          </Grid>
        );
      case "note":
        return (
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary">
              {block.content}
            </Typography>
          </Grid>
        );
      case "sms-consent":
        return (
          <Grid item xs={12}>
            <BlockTextProvider textBlocks={block.textBlocks}>
              <SmsConsentBlock />
            </BlockTextProvider>
          </Grid>
        );
      case "dwelling-info":
        return <DwellingInfoBlock control={control} source={block.source} />;
      case "address-display":
        return (
          <Grid item xs={12}>
            <AddressDisplayBlock source={block.source} />
          </Grid>
        );
      default:
        return null;
    }
  },
);

/**
 * Renders a single field within a Grid item, using the field renderer registry.
 */
export const FieldBlockRenderer = memo(
  ({ field, control }: FieldRendererProps) => {
    const Renderer = fieldRendererRegistry[field.type];
    if (!Renderer) return null;
    return (
      <Grid
        item
        xs={(field.gridSize?.xs ?? 12) as any}
        sm={field.gridSize?.sm as any}
        md={field.gridSize?.md as any}
      >
        <Renderer field={field} control={control} />
      </Grid>
    );
  },
);
